import { Box, IconButton, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AiSearchMonitoringService from '../../services/AiSearchMonitoringService';
import AiSearchService from '../../services/AiSearchService';
import { IAiSearchResultResponse } from '../../shared/interfaces/IAiSearchResultResponse';
import Helper from '../../shared/helpers/Helper';
import Accordion from '../Accordion/Accordion';
import Chip from '../Chip/Chip';
import Confirm from '../Confirm/Confirm';
import AiSearchResult from '../AiSearchResult/AiSearchResult';

interface Props {
  id: string;
  query: string;
  searchHash: string;
  country: string;
  date: string;
  onDelete: (id: string) => void;
}

const AiSearch = ({ id, query, searchHash, country, date, onDelete }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [requestedDataOnce, setRequestedDataOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // undefined = not loaded yet, null = loaded but no result row (pending),
  // object = a stored result (which may itself be empty).
  const [searchResult, setSearchResult] = useState<IAiSearchResultResponse | null>();

  const normalizedCountry =
    {
      de: 'de',
      uk: 'gb',
      at: 'at',
      ch: 'ch',
      nl: 'nl',
      us: 'us',
    }[country ? country.toLowerCase() : 'de'] ?? 'de';

  const loadLatestResult = () => {
    const controller = new AbortController();
    (async () => {
      const response = await AiSearchService.getLastSearchResult(
        searchHash,
        setLoading,
        controller.signal
      );
      setSearchResult(response ?? null);
      setLoaded(true);
    })();
    return () => controller.abort();
  };

  const toggleAccordion = () => {
    setIsExpanded((expanded) => !expanded);
    if (!requestedDataOnce) {
      setRequestedDataOnce(true);
      loadLatestResult();
    }
  };

  const deleteAiSearch = async () => {
    const response = await AiSearchMonitoringService.deleteMonitoredAiSearch(id);
    if (response && 'success' in response && response.success) onDelete(id);
  };

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={toggleAccordion}
      summary={
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QuestionAnswerIcon
              color="primary"
              sx={{ mr: theme.spacing(1) }}
            />
            <Chip
              size="small"
              label={query}
            />
            <Tooltip title={country.toUpperCase()}>
              <img
                src={`https://flagcdn.com/16x12/${normalizedCountry}.png`}
                srcSet={`https://flagcdn.com/32x24/${normalizedCountry}.png 2x, https://flagcdn.com/48x36/${normalizedCountry}.png 3x`}
                width="16"
                height="12"
                referrerPolicy="no-referrer"
                alt={normalizedCountry}
                style={{ marginLeft: theme.spacing(2) }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={Helper.getFormattedDate(date, false)}
              arrow
            >
              <Typography
                variant="body2"
                sx={{ mr: theme.spacing(1) }}
              >
                {Helper.getFormattedDate(date, true)}
              </Typography>
            </Tooltip>
            <IconButton
              color="error"
              size="small"
              onClick={() => setDeleteIsOpen(true)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Confirm
              text={t('delete_confirm')}
              open={deleteIsOpen}
              onCancel={() => setDeleteIsOpen(false)}
              onOk={deleteAiSearch}
            />
          </Box>
        </>
      }
      details={
        <>
          {isExpanded && loading && <LinearProgress sx={{ my: theme.spacing(1) }} />}
          {isExpanded && !loading && loaded && <AiSearchResult searchResult={searchResult} />}
        </>
      }
    />
  );
};

export default AiSearch;
